/**
 * Tournaments List Page
 * Route: /prioritization/tournaments
 *
 * Lists all tournaments grouped by state.
 */

import { useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import {
  Trophy,
  Plus,
  Clock,
  CheckCircle,
  Circle,
  ChevronRight,
  Layers,
} from 'lucide-react';
import { useTournamentStore } from '../../../stores/prioritization/tournamentStore';
import type { Tournament } from '../../../types/prioritization/tournament';

// ─── State badge ──────────────────────────────────────────────────────────────

function StateBadge({ state }: { state: Tournament['state'] }) {
  const map: Record<Tournament['state'], { label: string; className: string }> = {
    DRAFT:            { label: 'Draft',        className: 'bg-[var(--hover-tint)] text-[var(--text-sec)]' },
    READY:            { label: 'Ready',        className: 'bg-[var(--slate-dim)] text-[var(--text-sec)]' },
    IN_PROGRESS:      { label: 'In Progress',  className: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
    AWAITING_ADVANCE: { label: 'Needs Review', className: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
    COMPLETE:         { label: 'Complete',     className: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' },
    CANCELLED:        { label: 'Cancelled',    className: 'bg-[var(--coral-dim)] text-[var(--coral)]' },
  };
  const { label, className } = map[state] ?? { label: state, className: 'bg-[var(--hover-tint)] text-[var(--text-sec)]' };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${className}`}>
      {label}
    </span>
  );
}

// ─── Tournament card ──────────────────────────────────────────────────────────

function TournamentCard({ t }: { t: Tournament }) {
  const roundsCompleted = t.rounds.filter(r => r.state === 'COMPLETE').length;
  const totalRounds     = t.rounds.length;
  const itemCount       = t.itemIds?.length ?? 0;

  return (
    <Link
      to={`/prioritization/tournaments/${t.id}`}
      className="flex flex-nowrap items-center justify-between bg-[var(--bg-raised)] border border-[var(--rule)] px-4 sm:px-5 py-4 hover:border-[var(--rule)] hover:shadow-sm transition-all group"
    >
      <div className="flex items-start gap-3 min-w-0">
        <div className="mt-0.5 p-2 bg-[var(--surface)] shrink-0">
          <Trophy className="h-4 w-4 text-[var(--text-ter)]" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-[var(--text-pri)] truncate">{t.name}</p>
            <StateBadge state={t.state} />
          </div>
          {t.description && (
            <p className="text-xs text-[var(--text-ter)] mt-0.5 line-clamp-1">{t.description}</p>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 text-xs text-[var(--text-ter)]">
            <span className="flex items-center gap-1">
              <Layers className="h-3 w-3" />
              {totalRounds} round{totalRounds !== 1 ? 's' : ''}
              {totalRounds > 0 && ` · ${roundsCompleted}/${totalRounds} complete`}
            </span>
            {itemCount > 0 && (
              <span>{itemCount} items</span>
            )}
            {t.config.defaultToolId && (
              <span className="capitalize">{t.config.defaultToolId.replace(/_/g, ' ')}</span>
            )}
          </div>
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-[var(--text-ter)] group-hover:text-[var(--text-ter)] shrink-0 ml-2 sm:ml-4 transition-colors" />
    </Link>
  );
}

// ─── Section ──────────────────────────────────────────────────────────────────

function Section({
  title, icon: Icon, iconClass, items,
}: {
  title: string;
  icon: typeof Clock;
  iconClass: string;
  items: Tournament[];
}) {
  if (items.length === 0) return null;
  return (
    <section className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`h-4 w-4 ${iconClass}`} />
        <h2 className="text-sm font-semibold text-[var(--text-sec)]">{title}</h2>
        <span className="text-xs text-[var(--text-ter)] ml-1">({items.length})</span>
      </div>
      <div className="space-y-2">
        {items.map(t => <TournamentCard key={t.id} t={t} />)}
      </div>
    </section>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function TournamentsList() {
  const navigate        = useNavigate();
  const tournamentMap   = useTournamentStore(s => s.tournaments);
  const tournaments     = useMemo(() => Object.values(tournamentMap), [tournamentMap]);
  const loadTournaments = useTournamentStore(s => s.loadTournaments);

  // Reconstruct from DB on mount (Build Contract §2.4 — refresh-safe)
  useEffect(() => { loadTournaments(); }, [loadTournaments]);

  const { active, draft, completed } = useMemo(() => {
    const active    = tournaments.filter(t => ['READY', 'IN_PROGRESS', 'AWAITING_ADVANCE'].includes(t.state));
    const draft     = tournaments.filter(t => t.state === 'DRAFT');
    const completed = tournaments.filter(t => ['COMPLETE', 'CANCELLED'].includes(t.state));
    return { active, draft, completed };
  }, [tournaments]);

  const isEmpty = tournaments.length === 0;

  return (
    <div className="max-w-3xl mx-auto py-8 px-0 sm:px-6">
      <PageHeader
        icon={<Trophy className="h-4 w-4 text-[var(--text-sec)]" />}
        iconBg="bg-[var(--hover-tint)]"
        title="Tournaments"
        subtitle="Multi-round prioritization brackets for culling large item pools"
        actions={
          <button
            onClick={() => navigate('/prioritization/tournaments/new')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Tournament
          </button>
        }
      />

      {isEmpty ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="p-4 bg-[var(--surface)] mb-4">
            <Trophy className="h-10 w-10 text-[var(--text-ter)]" />
          </div>
          <h2 className="text-base font-semibold text-[var(--text-sec)] mb-1">No tournaments yet</h2>
          <p className="text-sm text-[var(--text-ter)] max-w-sm mb-6">
            Create a tournament to narrow a large item pool through successive ranked sessions — like a bracket system.
          </p>
          <button
            onClick={() => navigate('/prioritization/tournaments/new')}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create your first tournament
          </button>
        </div>
      ) : (
        <>
          <Section title="Active"    icon={Clock}        iconClass="text-[var(--mustard)]" items={active}    />
          <Section title="Draft"     icon={Circle}       iconClass="text-[var(--text-ter)]"  items={draft}     />
          <Section title="Completed" icon={CheckCircle}  iconClass="text-[var(--green)] dark:text-[var(--text-ter)]" items={completed} />
        </>
      )}
    </div>
  );
}

export default TournamentsList;
